import React from "react";
import { Dialog, DialogContent } from "@mui/material";
import { OVERVIEW_CONFIGS } from "../constants/overviewConfigs";
import ModalHeader from "../shared/ModalHeader";
import ModalActions from "../shared/ModalActions";

const dialogSx = {
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    background: "#1a1a1a",
    color: "text.primary",
    maxHeight: "calc(100vh - 64px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    m: 2,
  },
};

const scrollbarSx = {
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.05)" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.2)",
    borderRadius: "4px",
  },
};

/**
 * DetailModal
 *
 * Props:
 *  detailSection  {string|null}
 *  detailIndex    {number|null}
 *  detailDraft    {object|null}
 *  setDetailDraft {fn}
 *  form           {object}
 *  setForm        {fn}
 *  effectiveId    {string|null}
 *  saveToBackend  {fn}
 *  onClose        {fn}
 */
const DetailModal = ({
  detailSection,
  detailIndex,
  detailDraft,
  setDetailDraft,
  form,
  setForm,
  effectiveId,
  saveToBackend,
  onClose,
}) => {
  if (!detailSection || detailIndex === null || !detailDraft) return null;

  const config = OVERVIEW_CONFIGS[detailSection];
  if (!config) return null;

  const data = form[detailSection];
  const items =
    detailSection === "pricing" ? data.packages : data[config.itemsKey];
  const isNew = detailIndex >= items.length;
  const itemsKey = detailSection === "pricing" ? "packages" : config.itemsKey;

  const handleSave = () => {
    const next = [...items];
    next[detailIndex] = detailDraft;

    // Build updated form snapshot for auto-save
    const updatedForm = {
      ...form,
      [detailSection]: {
        ...form[detailSection],
        [itemsKey]: next,
      },
    };

    setForm(updatedForm);

    // Auto-save with latest snapshot — no stale closure
    setTimeout(() => {
      if (effectiveId) saveToBackend(updatedForm);
    }, 1000);

    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      sx={dialogSx}
    >
      <ModalHeader
        title={`${isNew ? "Add" : "Edit"} ${config.itemLabel} #${detailIndex + 1}`}
        subtitle={config.getDraftLabel(detailDraft) || "Fill in the details below"}
        onClose={onClose}
      />

      <DialogContent
        sx={{ p: 3, pt: "20px", overflowY: "auto", flex: 1, minHeight: 0, ...scrollbarSx }}
      >
        {config.renderForm(detailDraft, setDetailDraft)}
      </DialogContent>

      <ModalActions
        onCancel={onClose}
        onConfirm={handleSave}
        confirmLabel={isNew ? `Add ${config.itemLabel}` : "Save Changes"}
        disabled={!config.isValid(detailDraft)}
      />
    </Dialog>
  );
};

export default DetailModal;